import { computed, nextTick, reactive } from 'vue';
import { createI18n } from 'vue-i18n';
import tenants, { isGlsTenant, TENANT_IDS } from '@/config/tenants.js';
import locales from '@/config/locales.js';
import config from '@/config/defaultConfig.js';
import get from '@/util/get.js';

function setupI18n(locale, i18nOptions, isWidget, tenantId, themeId) {
  // be aware that this is not registered via app.use but instead proxied through rttI18n
  // so that locale changes always go through rttI18n
  // If you are missing a property add it below
  const i18n = createI18n({
    locale,
    silentFallbackWarn: config.env !== 'development',
    silentTranslationWarn: config.env !== 'development',
    warnHtmlInMessage: 'off',
    messages: { en: {} },
    messageResolver: (obj, path) =>
      get(obj, path, '')?.replaceAll(/@/g, "{'@'}") || null,
    ...i18nOptions
  });
  setI18nLanguage(i18n, locale, isWidget, tenantId, themeId);
  return i18n;
}

function setI18nLanguage(i18n, locale, isWidget, tenantId, themeId) {
  if (i18n.mode === 'legacy') {
    i18n.global.locale = locale;
  } else {
    i18n.global.locale.value = locale;
  }
  /**
   * NOTE:
   * If you need to specify the language setting for headers, such as the `fetch` API, set it here.
   * The following is an example for axios.
   *
   * axios.defaults.headers.common['Accept-Language'] = locale
   */
  if (!isWidget) {
    document.querySelector('html').setAttribute('lang', locale);
    if (i18n.global.messages[locale]?.generic?.documentTitle) {
      let newTitle = i18n.global.messages[locale].generic.documentTitle;

      if (themeId === 'bettermile' || !isGlsTenant(tenantId)) {
        newTitle = newTitle.replace('GLS', '');
      }
      document.title = newTitle;
    }
  }
}

async function loadLocaleMessages(i18n, locale) {
  // load locale messages with dynamic import
  const messages = await import(`../locales/${locale}.json`);

  // set locale and locale message
  i18n.global.setLocaleMessage(locale, messages.default);

  return nextTick();
}

// i18n.te function is not fallback aware
function translateOrEmpty(translateFunction, key, props) {
  const translation = translateFunction(key, props);
  if (translation === key) return '';
  return translation;
}

function tOrEmpty(i18n, key, props) {
  return translateOrEmpty(i18n.t, key, props);
}

export function getStartLocale(locale) {
  const browserLocale = navigator.language;
  const browserLanguage = navigator.language.slice(0, 2);
  const supportedLocales = Object.keys(locales);

  // TODO change order : need to make rttI18n also the truth instead of only router
  if (locale) return locale;
  if (supportedLocales.includes(browserLocale)) return browserLocale;
  if (supportedLocales.includes(browserLanguage)) return browserLanguage;
  return 'en';
}

// is it not a real plugin because
export default function createRttI18n({
  locale: startLocale,
  tenantId,
  isWidget,
  themeId,
  i18nOptions = {}
}) {
  const i18n = setupI18n(startLocale, i18nOptions, isWidget, tenantId, themeId);
  const vueI18n = i18n.global;

  const $rttI18n = reactive({
    i18n,
    isLocaleDirty: false,
    locale: startLocale,
    tenantId: tenantId,
    async setLocale(newLocale) {
      if (newLocale.includes('-'))
        await Promise.all([
          loadLocaleMessages(i18n, newLocale.substring(0, 2)),
          loadLocaleMessages(i18n, newLocale)
        ]);
      else await loadLocaleMessages(i18n, newLocale);

      setI18nLanguage(i18n, newLocale, isWidget, tenantId, themeId);
      this.isLocaleDirty = true;
      this.locale = newLocale;
    },
    setTenantId(newTenant) {
      this.tenantId = newTenant;
    },
    $isWidget: isWidget,
    isGlsAtSelected: computed(() => $rttI18n.tenantId === TENANT_IDS.GLS_AT),
    isGlsEsSelected: computed(() => $rttI18n.tenantId === TENANT_IDS.GLS_ES),
    isGlsHuSelected: computed(() => $rttI18n.tenantId === TENANT_IDS.GLS_HU),
    isGlsItSelected: computed(() => $rttI18n.tenantId === TENANT_IDS.GLS_IT),
    isRoyalMailSelected: computed(
      () =>
        $rttI18n.tenantId === TENANT_IDS.ROYALMAIL_UK ||
        $rttI18n.tenantId === TENANT_IDS.ROYALMAIL_UK_TEST
    ),
    isGlsPlSelected: computed(() => $rttI18n.tenantId === TENANT_IDS.GLS_PL),
    isGlsUsSelected: computed(
      () =>
        $rttI18n.tenantId === TENANT_IDS.GLS_US ||
        $rttI18n.tenantId === TENANT_IDS.GLS_US_TEST
    ),
    isGlsTenant: computed(() => isGlsTenant($rttI18n.tenantId)),
    country: computed(() => tenants[$rttI18n.tenantId]?.country.code),
    tenantName: computed(() => tenants[$rttI18n.tenantId]?.name),
    getLocaleForTenantChange(tenantId) {
      return this.isLocaleDirty
        ? this.locale
        : tenants[tenantId].country.defaultLocale.code;
    },
    getLocaleWithDefaultCountryCode() {
      return this.locale.includes('-')
        ? this.locale
        : `${this.locale}-${locales[this.locale].defaultRegion}`;
    },
    tenantSpecificContent(keyPostFix, props) {
      const key = `tenantSpecificContent.${this.tenantId}.${keyPostFix}`;
      return tOrEmpty(vueI18n, key, props);
    },
    tenantSpecificContentOrGeneric(keyPostFix, props) {
      return (
        this.tenantSpecificContent(keyPostFix, props) ||
        vueI18n.t(`tenantSpecificContent.generic.${keyPostFix}`, this.locale, {
          tenantId: this.tenantId,
          country: this.country,
          ...props
        })
      );
    },
    tenantSpecificContentExists(keyPostFix) {
      const key = `tenantSpecificContent['${this.tenantId}'].${keyPostFix}`;
      return tOrEmpty(vueI18n, key);
    },
    /**
     * Retrieves a tenant-specific translation key with a fallback option.
     *
     * This method constructs a tenant-specific key based on the tenant ID and the provided key postfix.
     * It attempts to fetch the localized value for that key. If the key is not found,
     * it falls back to the provided fallback key or the key postfix if no fallback is specified.
     *
     * @param {string} keyPostFix - The postfix to append to the tenant-specific key.
     * @param {string} [fallback=undefined] - (Optional) A fallback key to use if the tenant-specific key is not found.
     *                                        If not provided, the key postfix is used as the fallback.
     * @param {object} props - Properties to pass to the translation function.
     * @returns {string} - The localized string for the tenant-specific key or the fallback key.
     *
     * @example
     * // Assuming tenantId is "123" and the locale system has the relevant keys:
     * // vueI18n.t('tenantSpecificContent.123.greeting') => 'Hello'
     * tenantSpecificWithFallback('greeting'); // Returns: 'Hello'
     *
     * // If the tenant-specific key is missing, but 'greeting' exists globally:
     * tenantSpecificWithFallback('nonExistentKey', 'greeting'); // Returns: 'Hello'
     *
     * // If neither tenant-specific nor fallback keys exist:
     * tenantSpecificWithFallback('nonExistentKey'); // Returns: ''
     */
    tenantSpecificWithFallback(keyPostFix, fallback = undefined, props) {
      const key = `tenantSpecificContent['${this.tenantId}'].${keyPostFix}`;
      if (fallback === undefined) fallback = keyPostFix;
      return tOrEmpty(vueI18n, key, props) || vueI18n.t(fallback, props);
    },
    tcOrEmpty(key, props) {
      return translateOrEmpty(vueI18n.t, key, props);
    },
    getSlavicPluralDeclinationKeySuffix(n) {
      const lastDigit = n % 10;
      return [0, 1, 5, 6, 7, 8, 9].includes(lastDigit) ||
        [12, 13, 14].includes(n)
        ? 'FiveToTen'
        : 'TwoToFour';
    }
  });

  return {
    get $rttI18nInstance() {
      return $rttI18n;
    },
    install(app) {
      // eslint-disable-next-line no-console
      $rttI18n.setLocale($rttI18n.locale).catch(console.warn);
      app.config.globalProperties.$t = vueI18n.t;
      app.config.globalProperties.$te = vueI18n.te; // be aware that this is not fallback aware
      app.config.globalProperties.$rttI18n = $rttI18n;

      app.provide('rttI18n', $rttI18n);
      app.provide('t', vueI18n.t);
    }
  };
}
